import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Cross } from 'icons/Cross'
import { getLocalStorageItem, setLocalStorageItem } from 'utils/LocalStorage'
import { media } from 'utils/media'
import { withSilentFailure } from 'utils/withSilentFailure'
import { Link } from 'lib/Link'
import { Text } from 'lib/Text'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { useUiContext } from './UiContext'

const CookieSection = styled.div`
  display: grid;
  grid-template-columns: 60px auto 60px;
  background: black;
  min-height: 60px;
  order: -1;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
`

const TextContainer = styled.div`
  grid-column: 2 / 3;
  display: flex;
  justify-content: center;
  align-items: center;

  ${media.tabletDown} {
    grid-column: 1 / 3;
  }
`

const Message = styled(Text)`
  color: white;
  padding: 20px;
`

const PolicyLink = styled(Link)`
  color: white;
  text-decoration-color: white;

  &:hover {
    color: white;
    text-decoration-color: white;
  }
`

const CloseButton = styled.button`
  border: none;
  background: none;
  appearance: none;
  cursor: pointer;
  grid-column: 3 / 4;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 20px;
  }
`

export const CookieNotice = () => {
  const [cookiesAccepted, setCookiesAccepted] = useState<boolean>(true)
  const { isLoggedIn, sessionIsInitialized } = useCurrentUser()
  const { menuOpen } = useUiContext()

  const acceptCookies = useCallback(() => {
    setCookiesAccepted(true)
    setLocalStorageItem('cookiesAccepted', 'true')
  }, [setCookiesAccepted])

  /**
   * If the cookie notice hasn't been dismissed, and the user is not logged in,
   * show the cookie notice. Also, if the cookie notice hasn't been dismissed, but
   * they ARE logged in, go ahead and hide it. They've accepted terms by signing up.
   */
  useEffect(() => {
    withSilentFailure(() => {
      if (getLocalStorageItem('cookiesAccepted') !== 'true') {
        if (isLoggedIn) {
          acceptCookies()
        } else if (sessionIsInitialized) {
          setCookiesAccepted(false)
        }
      }
    })
  }, [isLoggedIn, sessionIsInitialized, acceptCookies])

  // When the user opens the menu, hide the cookie notice.
  useEffect(() => {
    if (menuOpen && !cookiesAccepted) {
      acceptCookies()
    }
  }, [menuOpen, acceptCookies, cookiesAccepted])

  if (cookiesAccepted) {
    return null
  }

  return (
    <CookieSection role="dialog" aria-modal="false">
      <TextContainer>
        <Message weight="semi" size="small" color="white">
          We use cookies to provide you with a better service and for promotional purposes. By continuing to use this site you consent to our use of
          cookies as described in our{' '}
          <PolicyLink href="/legal/cookie-policy" prefetch={false}>
            cookie policy
          </PolicyLink>
        </Message>
      </TextContainer>

      <CloseButton onClick={acceptCookies} aria-label="close cookie notice">
        <Cross />
      </CloseButton>
    </CookieSection>
  )
}
